import { use } from "react";

import { Skeleton } from "../../../components/skeleton";
import { useMultiplayerContext } from "../../../hooks/use-multiplayer-context";
import { UpdateTranscriptSelectionType } from "../../../hooks/use-transcript-selection";
import {
  APITranscript,
  AsyncStatus,
  Stage,
  TranscriptionConfiguration,
  TranscriptMetadata,
  TranscriptSelection,
} from "../../../types/types";
import { prettyDateTime } from "../../../utils/pretty-date-time";
import { TranscriptEditAction } from "../../../utils/transcript-revision-utils";
import { TranscriptNameContextProvider } from "../../historical-transcripts/historical-transcripts-table/transcript-name/transcript-name-context";
import { TranscriptPermissionsContext } from "../transcript-permissions-provider";
import { ReadOnlyCallout } from "./read-only-callout";
import { Toolbar } from "./toolbar";
import { TranscriptName } from "./transcript-name";
import { UserPresenceList } from "./user-presence-list";

interface TranscriptHeaderProps {
  metadata?: TranscriptMetadata;
  stage: Stage;
  transcript: APITranscript;
  version: number;
  transcriptId: string;
  transcriptionConfiguration: TranscriptionConfiguration;
  setTranscriptionConfiguration: React.Dispatch<
    React.SetStateAction<TranscriptionConfiguration>
  >;
  onEditOperationError: (error: Error, action: TranscriptEditAction) => void;
  transcriptSelection: TranscriptSelection | null;
  updateTranscriptSelection: UpdateTranscriptSelectionType;
  scrollTranscriptToTime: (timeMs: number) => Promise<void>;
  undo: () => void;
  redo: () => void;
  backgroundAlignerStatus: AsyncStatus;
  syncScrollToTime: boolean;
  setSyncScrollToTime: React.Dispatch<React.SetStateAction<boolean>>;
  scrollTranscriptToCurrentTime: () => void;
  seekMedia: (options: {
    timeSeconds: number;
    play?: boolean;
    scroll?: boolean;
  }) => void;
  currentTime: number;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
  hasMedia: boolean;
  scrollTranscriptToSelection: (
    selection: TranscriptSelection,
    options?: { top?: number; bottom?: number },
  ) => Promise<void>;
}

export function TranscriptHeader(props: TranscriptHeaderProps) {
  const {
    stage,
    metadata,
    transcript,
    version,
    transcriptId,
    transcriptionConfiguration,
    setTranscriptionConfiguration,
    onEditOperationError,
    transcriptSelection,
    updateTranscriptSelection,
    scrollTranscriptToTime,
    undo,
    redo,
    backgroundAlignerStatus,
    syncScrollToTime,
    setSyncScrollToTime,
    seekMedia,
    currentTime,
    setPaused,
    hasMedia,
    scrollTranscriptToSelection,
  } = props;

  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const { connectedUsers, authenticated, sessionId } = useMultiplayerContext();

  if (!metadata) {
    return (
      <div className="ml-16">
        <Skeleton height={20} width={200} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[752px] pl-16">
      {metadata?.expires && (
        <span
          className="overflow-hidden text-sm text-ellipsis whitespace-nowrap text-gray-600"
          title={`Available until ${prettyDateTime(metadata.expires)}`}
        >
          Available until {prettyDateTime(metadata.expires)}
        </span>
      )}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 overflow-hidden">
            <TranscriptNameContextProvider key={metadata?.id}>
              {metadata ? (
                <TranscriptName name={metadata.name} id={metadata.id} />
              ) : (
                <TranscriptName
                  name={transcriptionConfiguration.name || ""}
                  id={transcriptId}
                  setTranscriptionConfiguration={setTranscriptionConfiguration}
                />
              )}
            </TranscriptNameContextProvider>
          </div>
          <div className="mr-3">
            {authenticated && connectedUsers.length > 0 && (
              <UserPresenceList
                connectedUsers={connectedUsers}
                currentUserSessionId={sessionId}
                scrollTranscriptToSelection={scrollTranscriptToSelection}
                transcript={transcript}
              />
            )}
          </div>
        </div>
        {transcriptPermissions.edit && (
          <Toolbar
            stage={stage}
            metadata={metadata}
            transcript={transcript}
            version={version}
            transcriptId={transcriptId}
            onEditOperationError={onEditOperationError}
            transcriptSelection={transcriptSelection}
            updateTranscriptSelection={updateTranscriptSelection}
            scrollTranscriptToTime={scrollTranscriptToTime}
            undo={undo}
            redo={redo}
            backgroundAlignerStatus={backgroundAlignerStatus}
            syncScrollToTime={syncScrollToTime}
            setSyncScrollToTime={setSyncScrollToTime}
            scrollTranscriptToCurrentTime={() =>
              scrollTranscriptToTime(currentTime)
            }
            seekMedia={seekMedia}
            currentTime={currentTime}
            setPaused={setPaused}
            hasMedia={hasMedia}
          />
        )}
        {!transcriptPermissions.edit && <ReadOnlyCallout />}
      </div>
    </div>
  );
}
